import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Phone, Smartphone, Calculator, Shield, Globe, Check, Wand2, LogIn, LogOut, Search, Loader2 } from "lucide-react";
import { useState } from "react";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [quoteQ, setQuoteQ] = useState("");
  const [quoteBusy, setQuoteBusy] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t, lang, setLang, langs } = useT();
  const { user, logout } = useAuth();
  const isLoggedIn = !!user;
  const onAdmin = pathname.startsWith("/admin");
  if (onAdmin) return null;

  // Inline quote-number lookup (next to Backend). Hits the scoped lookup endpoint and
  // navigates the user straight to the public quote page on success.
  const submitQuoteSearch = async (e) => {
    e?.preventDefault?.();
    const q = quoteQ.trim().toUpperCase();
    if (!q) return;
    setQuoteBusy(true);
    try {
      const { data } = await api.get(`/quotes/by-number/${encodeURIComponent(q)}`);
      setQuoteQ("");
      setOpen(false);
      navigate(`/quote/${data.id}`);
    } catch (err) {
      toast.error(err.response?.status === 404 ? "Quote not found in your scope" : (formatApiErrorDetail(err.response?.data?.detail) || err.message));
    } finally { setQuoteBusy(false); }
  };

  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors ${
      isActive ? "text-[#0B132B]" : "text-[#475569] hover:text-[#008B99]"
    }`;

  const currentLang = langs.find((l) => l.code === lang) || langs[0];

  const LangPicker = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 text-xs font-semibold mono uppercase tracking-widest border border-slate-300 px-2.5 py-1.5 hover:border-[#0B132B] hover:text-[#0B132B] text-slate-600 transition-colors"
          data-testid="lang-toggle"
          aria-label="Select language"
        >
          <Globe className="h-3.5 w-3.5" />
          {currentLang.short}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none border-slate-300 min-w-[10rem]" data-testid="lang-menu">
        {langs.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={() => setLang(l.code)}
            className="rounded-none cursor-pointer flex items-center justify-between gap-3"
            data-testid={`lang-option-${l.code}`}
          >
            <span className="font-medium">{l.label}</span>
            <span className="mono text-[10px] tracking-widest text-slate-500 inline-flex items-center gap-1">
              {l.short}
              {l.code === lang && <Check className="h-3 w-3 text-[#008B99]" />}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/70"
      data-testid="site-header"
    >
      <div className="tw-container flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-home-link">
          <div className="h-8 w-8 bg-[#0B132B] flex items-center justify-center">
            <Phone className="h-4 w-4 text-[#00E5FF]" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-extrabold text-[#0B132B] tracking-tight text-base">TW Calc</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
          <NavLink to="/" className={linkCls} end data-testid="nav-home">{t("nav_home")}</NavLink>
          <NavLink to="/estimator" className={linkCls} data-testid="nav-estimator">
            <span className="inline-flex items-center gap-1.5"><Calculator className="h-4 w-4" /> {t("nav_estimator")}</span>
          </NavLink>
          <NavLink to="/wizard" className={linkCls} data-testid="nav-wizard">
            <span className="inline-flex items-center gap-1.5"><Wand2 className="h-4 w-4" /> Bundle</span>
          </NavLink>
          <NavLink to="/devices" className={linkCls} data-testid="nav-devices">
            <span className="inline-flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> {t("nav_devices")}</span>
          </NavLink>
          {isLoggedIn && (
            <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1.5"><Shield className="h-4 w-4" /> Backend</span>
            </NavLink>
          )}
          {isLoggedIn && (
            <form
              onSubmit={submitQuoteSearch}
              className="hidden lg:flex items-center gap-1.5 border border-slate-300 bg-white px-2.5 py-1.5 focus-within:border-[#0B132B] transition-colors"
              data-testid="header-quote-search-form"
            >
              <Search className="h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={quoteQ}
                onChange={(e) => setQuoteQ(e.target.value)}
                placeholder="Quote #"
                className="bg-transparent outline-none text-xs mono uppercase tracking-widest w-24"
                data-testid="header-quote-search-input"
              />
              <button
                type="submit"
                disabled={quoteBusy || !quoteQ.trim()}
                className="text-[10px] font-bold tracking-widest uppercase text-[#0B132B] hover:text-[#008B99] disabled:opacity-40"
                data-testid="header-quote-search-submit"
              >
                {quoteBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Go"}
              </button>
            </form>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangPicker />
          {isLoggedIn ? (
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/"); }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold mono uppercase tracking-widest border border-slate-300 px-3 py-2 hover:border-[#0B132B] hover:text-[#0B132B] text-slate-600 transition-colors"
              data-testid="header-logout-btn"
              aria-label="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          ) : (
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-1.5 text-xs font-semibold mono uppercase tracking-widest border border-slate-300 px-3 py-2 hover:border-[#0B132B] hover:text-[#0B132B] text-slate-600 transition-colors"
              data-testid="header-login-btn"
            >
              <LogIn className="h-3.5 w-3.5" /> Sign in
            </Link>
          )}
          <Link to={isLoggedIn ? "/wizard" : "/admin/login?next=%2Fwizard"} className="btn-brand !py-2.5 !px-5 text-sm" data-testid="header-cta-bundle">
            {isLoggedIn ? "Open Bundle" : "Sign in"}
          </Link>
        </div>

        <button
          className="md:hidden p-2 border border-slate-300"
          onClick={() => setOpen((s) => !s)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5 text-[#0B132B]" />
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="tw-container py-4 flex flex-col gap-3">
            <NavLink to="/" className={linkCls} onClick={() => setOpen(false)} end>{t("nav_home")}</NavLink>
            <NavLink to="/estimator" className={linkCls} onClick={() => setOpen(false)}>{t("nav_estimator")}</NavLink>
            <NavLink to="/wizard" className={linkCls} onClick={() => setOpen(false)}>Bundle</NavLink>
            <NavLink to="/devices" className={linkCls} onClick={() => setOpen(false)}>{t("nav_devices")}</NavLink>
            {isLoggedIn && (
              <NavLink to="/admin" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-admin">
                Backend
              </NavLink>
            )}
            {isLoggedIn && (
              <form
                onSubmit={submitQuoteSearch}
                className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 focus-within:border-[#0B132B]"
                data-testid="mobile-quote-search-form"
              >
                <Search className="h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  value={quoteQ}
                  onChange={(e) => setQuoteQ(e.target.value)}
                  placeholder="Find quote #"
                  className="bg-transparent outline-none text-sm mono uppercase tracking-widest flex-1"
                  data-testid="mobile-quote-search-input"
                />
                <button
                  type="submit"
                  disabled={quoteBusy || !quoteQ.trim()}
                  className="text-xs font-bold tracking-widest uppercase text-[#0B132B] hover:text-[#008B99] disabled:opacity-40"
                  data-testid="mobile-quote-search-submit"
                >
                  {quoteBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Go"}
                </button>
              </form>
            )}
            {isLoggedIn ? (
              <button
                type="button"
                onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                className="text-left text-sm font-medium tracking-wide text-[#475569] hover:text-[#008B99]"
                data-testid="mobile-logout-btn"
              >
                Sign out
              </button>
            ) : (
              <Link to="/admin/login" className={linkCls({ isActive: false })} onClick={() => setOpen(false)} data-testid="mobile-login-link">
                Sign in
              </Link>
            )}
            <div className="pt-2"><LangPicker /></div>
          </div>
        </div>
      )}
    </header>
  );
}
